import { useI18n } from "@/hooks/useI18n";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useDrawers } from "@/hooks/useDrawers";
import { Link } from "react-router-dom";
import { 
  Flower, 
  Mail, 
  MessageSquare,
  MapPin,
  Phone
} from "lucide-react";

export const Footer = () => {
  const { t } = useI18n();

  const quickLinks = [
    { key: 'nav.catalog', href: '/catalog' },
    { key: 'nav.farms', href: '/farms' },
    { key: 'nav.logistics', href: '/logistics' },
    { key: 'nav.about', href: '/about' },
  ];

  const legalLinks = [
    { key: 'footer.privacy', href: '/privacy' },
    { key: 'footer.terms', href: '/terms' },
    { key: 'footer.cookies', href: '/cookies' },
  ];

  return (
    <footer className="bg-andean-forest text-andean-cream">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1 space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-andean rounded-lg flex items-center justify-center">
                <Flower className="w-5 h-5 text-andean-cream" />
              </div>
              <span className="font-display font-light text-base tracking-wide">
                Andean Bloom
              </span>
            </Link>
            <p className="text-andean-cream/80 text-sm leading-relaxed">
              Connecting Ecuador's finest flower farms with premium buyers through innovative logistics and transparent partnerships.
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-light text-base tracking-wide">Quick Links</h3>
            <nav className="space-y-2">
              {quickLinks.map((link) => (
                <Link
                  key={link.key}
                  to={link.href}
                  className="block text-sm text-andean-cream/80 hover:text-andean-cream transition-colors"
                >
                  {t(link.key)}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="font-light text-base tracking-wide">{t('footer.contact')}</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-andean-cream/60" />
                 <a 
                  href="mailto:sales@andean-bloom.com"
                  className="text-andean-cream/80 hover:text-andean-cream transition-colors"
                >
                  sales@andean-bloom.com
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-andean-cream/60" />
                <a 
                  href="tel:+593981931367"
                  className="text-andean-cream/80 hover:text-andean-cream transition-colors"
                >
                  +593 98 193 1367
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-andean-cream/60" />
                <span className="text-andean-cream/80">
                  Quito, Ecuador
                </span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-andean-cream/50 text-andean-cream bg-transparent hover:bg-andean-cream hover:text-andean-forest mt-2"
                asChild
              >
                <a 
                  href="https://wa.me/593981931367?text=Hello%20Andean%20Bloom" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t('footer.whatsapp')}
                </a>
              </Button>
            </div>
          </div>

          {/* Legal Links */}
          <div className="space-y-4">
            <h3 className="font-light text-base tracking-wide">{t('footer.legal')}</h3>
            <nav className="space-y-2">
              {legalLinks.map((link) => (
                <Link
                  key={link.key}
                  to={link.href}
                  className="block text-sm text-andean-cream/80 hover:text-andean-cream transition-colors"
                >
                  {t(link.key)}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        <Separator className="my-8 bg-andean-cream/20" />
        
        <div className="flex flex-col md:flex-row justify-between items-center text-sm text-andean-cream/60">
          <p>© 2025 Andean Bloom Exports. All rights reserved.</p>
          <p>Premium Ecuadorian Flowers • Global Delivery • Cold-Chain Guaranteed</p>
        </div>
      </div>
    </footer>
  );
};